import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import ChatView from "@/components/ChatView";
import ProjectsView from "@/components/ProjectsView";
import SnippetsView from "@/components/SnippetsView";
import SettingsView from "@/components/SettingsView";
import MembersView from "@/components/MembersView";
import FeedbackView from "@/components/FeedbackView";
import BoardsView from "@/components/BoardsView";
import DocumentsView from "@/components/DocumentsView";
import BoardPage from "@/components/BoardPage";
import CanvasesView from "@/components/CanvasesView";
import CanvasPage from "@/components/CanvasPage";
import AIPanel from "@/components/AIPanel";
import CallPanel from "@/components/CallPanel";
import IncomingCall from "@/components/IncomingCall";
import AtenaPanel from "@/components/AtenaPanel";
import CommandPalette from "@/components/CommandPalette";
import FeedbackModal from "@/components/FeedbackModal";
import { ACTIVE_STATUSES , AWAITING_CONFIRMATION } from "@/components/FeedbackBadge";
import PostLoginUpdate from "@/components/PostLoginUpdate";
import * as avatarStore from "@/lib/avatarStore";
import { notification, ring, stopRing } from "@/utils/audio";
import { useIncomingCall } from "@/hooks/useIncomingCall";
import { INITIAL, initialSelection } from "@/lib/selection";
import { useEscape } from "@/hooks/useEscape";
import { trace, whatChanged } from "@/lib/trace";
import { Menu, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Workspace() {
  const { user } = useAuth();
  const { currentId, subscribe, refreshUnread } = useWorkspace();
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [myRole, setMyRole] = useState("member");
  const [opsConfig, setOpsConfig] = useState(null);
  const [feedbackTickets, setFeedbackTickets] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [feedbackDetailId, setFeedbackDetailId] = useState(null);

  const [selection, setSelection] = useState(INITIAL);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Open by default only where it is a column. Below xl it is a drawer over the
  // conversation, and a drawer that opens itself on arrival is in the way.
  const [aiOpen, setAiOpen] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1280px)").matches);
  // Which assistant occupies the right-hand panel. Atena is the default; the
  // VIBE.AI coding terminal is still one click away.
  const [panel, setPanel] = useState(() => localStorage.getItem("vs_side_panel") || "atena");
  const [call, setCall] = useState(null);  // { roomName, token, channelName } when a call panel is open
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  // ?board=<id>&window=1 opens one board on its own, sidebar and panels gone:
  // a second browser window for the board next to the chat in the first.
  const [standaloneBoard, setStandaloneBoard] = useState(false);
  const [canvases, setCanvases] = useState([]);
  const [canvasesLoading, setCanvasesLoading] = useState(false);
  const refreshCanvases = useCallback(async () => {
    if (!currentId) return;
    setCanvasesLoading(true);
    try { const r = await api.get("/canvases", { params: { workspace_id: currentId } }); setCanvases(r.data); }
    catch { /* guests get 403 */ }
    finally { setCanvasesLoading(false); }
  }, [currentId]);
  const refreshBoards = useCallback(async () => {
    if (!currentId) return;
    setBoardsLoading(true);
    try { const r = await api.get("/boards", { params: { workspace_id: currentId } }); setBoards(r.data); }
    catch { /* guests get 403; the nav item simply stays empty */ }
    finally { setBoardsLoading(false); }
  }, [currentId]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);   // /bug from the palette
  const [aiPrompt, setAiPrompt] = useState("");    // /claude from the palette

  const refreshChannels = useCallback(async () => {
    if (!currentId) return [];
    const r = await api.get("/channels", { params: { workspace_id: currentId } });
    setChannels(r.data);
    return r.data;
  }, [currentId]);

  const refreshUsers = useCallback(async () => {
    if (!currentId) return;
    // /users has no role; the members endpoint carries it (needed for isOwner gating).
    const [u, m] = await Promise.all([
      api.get("/users", { params: { workspace_id: currentId } }),
      api.get(`/workspaces/${currentId}/members`).catch(() => null),
    ]);
    const me = m?.data?.find((x) => x.id === user.id);
    if (me) setMyRole(me.role || "member");
    setUsers(u.data.filter((x) => x.id !== user.id));
  }, [currentId, user.id]);

  const refreshProjects = useCallback(async () => {
    if (!currentId) return;
    const r = await api.get("/projects", { params: { workspace_id: currentId } });
    setProjects(r.data);
  }, [currentId]);

  const refreshFeedback = useCallback(async () => {
    if (!currentId) return;
    setFeedbackLoading(true);
    try {
      const r = await api.get("/feedback", { params: { workspace_id: currentId, limit: 100 } });
      setFeedbackTickets(r.data.tickets || []);
    } finally { setFeedbackLoading(false); }
  }, [currentId]);

  useEffect(() => {
    if (!currentId) {
      setOpsConfig(null);
      return;
    }
    api.get("/ops/config", { params: { workspace_id: currentId } })
      .then(({ data }) => setOpsConfig(data))
      .catch(() => setOpsConfig(null));
  }, [currentId]);

  const upsertTicket = useCallback((ticket) => {
    setFeedbackTickets((prev) => {
      const i = prev.findIndex((t) => t.id === ticket.id);
      if (i === -1) return [ticket, ...prev];
      const next = [...prev]; next[i] = ticket; return next;
    });
  }, []);

  // A navigation the person performed, as opposed to the default the initial
  // effect applies when it finally finishes loading. Every user-driven path
  // goes through `choose`, so the late default knows to stand down.
  const chosenRef = useRef(false);
  const choose = useCallback((nextSelection) => {
    chosenRef.current = true;
    trace("choose", { to: nextSelection });
    setSelection(nextSelection);
  }, []);

  const selectAndCloseMobileNav = useCallback((nextSelection) => {
    choose(nextSelection);
    setMobileSidebarOpen(false);
  }, [choose]);

  const openFeedbackTicket = useCallback((ticketId) => {
    selectAndCloseMobileNav({ kind: "feedback" });
    setFeedbackDetailId(ticketId);
  }, [selectAndCloseMobileNav]);

  const goToChannel = useCallback((channelId) => {
    selectAndCloseMobileNav({ kind: "channel", id: channelId });
  }, [selectAndCloseMobileNav]);

  const initDepsRef = useRef(null);
  const initRunRef = useRef(0);

  useEffect(() => {
    if (!currentId) return;
    const deps = [currentId, refreshChannels, refreshUsers, refreshProjects,
                  refreshFeedback, refreshBoards, refreshCanvases];
    const run = ++initRunRef.current;
    trace("init:start", {
      run, currentId,
      changed: whatChanged(initDepsRef.current, deps,
                           ["currentId", "refreshChannels", "refreshUsers", "refreshProjects",
                            "refreshFeedback", "refreshBoards", "refreshCanvases"]),
      resettingChosen: chosenRef.current,
    });
    initDepsRef.current = deps;
    chosenRef.current = false;
    setSelection(INITIAL);
    (async () => {
      const step = async (name, fn) => {
        const at = Date.now();
        const out = await fn();
        trace("init:step", { run, name, ms: Date.now() - at, chosen: chosenRef.current });
        return out;
      };
      const chans = await step("channels", refreshChannels);
      await step("users", refreshUsers);
      await step("projects", refreshProjects);
      await step("feedback", refreshFeedback);
      refreshBoards();
      refreshCanvases();
      // A choice made while all that was in flight outranks the default. It was
      // made by a person; this was made by a timeout.
      trace("init:tail", { run, chosen: chosenRef.current, channels: chans?.length ?? 0 });
      if (chosenRef.current) return;
      const open = initialSelection({ search: window.location.search, channels: chans,
                                      asWindow: new URLSearchParams(window.location.search).get("window") === "1" });
      if (!open) return;
      trace("init:apply", { run, to: open.selection });
      setSelection(open.selection);
      if (open.ticketId) setFeedbackDetailId(open.ticketId);
      if (open.standalone) setStandaloneBoard(true);
      // A standalone window keeps its URL, so a refresh reopens the same thing.
      if (!open.keepUrl) window.history.replaceState({}, "", window.location.pathname);
    })();
  }, [currentId, refreshChannels, refreshUsers, refreshProjects, refreshFeedback, refreshBoards, refreshCanvases]);

  // WS: auto-refresh channels/users + live feedback updates
  useEffect(() => {
    return subscribe((ev) => {
      if (ev.type === "channel_created" && ev.data?.workspace_id === currentId) {
        refreshChannels();
      } else if (ev.type === "channel_updated" && ev.data?.workspace_id === currentId) {
        refreshChannels();  // allowed_roles changed: the list itself may shrink
      } else if (ev.type === "member_joined" && ev.data?.workspace_id === currentId) {
        refreshUsers();
      } else if (ev.type === "feedback_created" && ev.ticket?.workspace_id === currentId) {
        setFeedbackTickets((prev) => prev.find((t) => t.id === ev.ticket.id) ? prev : [ev.ticket, ...prev]);
      } else if (ev.type === "feedback_update" && ev.ticket?.workspace_id === currentId) {
        setFeedbackTickets((prev) => prev.map((t) => t.id === ev.ticket.id ? ev.ticket : t));
      } else if (ev.type === "feedback_reply") {
        setFeedbackTickets((prev) => prev.map((t) => {
          if (t.id !== ev.ticket_id) return t;
          if ((t.replies || []).some((r) => r.id === ev.reply.id)) return t;  // dedup vs optimistic
          return { ...t, replies: [...(t.replies || []), ev.reply] };
        }));
      } else if (ev.type === "feedback_reply_updated") {
        setFeedbackTickets((prev) => prev.map((t) => t.id !== ev.ticket_id ? t : {
          ...t, replies: (t.replies || []).map((r) => r.id === ev.reply.id ? ev.reply : r),
        }));
      } else if (ev.type === "feedback_reply_deleted") {
        setFeedbackTickets((prev) => prev.map((t) => t.id !== ev.ticket_id ? t : {
          ...t, replies: (t.replies || []).filter((r) => r.id !== ev.reply_id),
        }));
      } else if (ev.type === "agent_run_finished") {
        // The run's output already arrives as a feedback_reply; this only surfaces
        // the outcome to whoever is not looking at the ticket.
        toast[ev.exit_code === 0 ? "success" : "error"](
          ev.exit_code === 0
            ? "Claude Code a terminat — vezi reply-ul pe ticket."
            : `Claude Code a ieșit cu codul ${ev.exit_code}.`
        );
      } else if ((ev.type === "board_upsert" && ev.board?.workspace_id === currentId) || ev.type === "board_deleted") {
        refreshBoards();
        if (ev.type === "board_deleted") setSelection((sel) => sel.kind === "board" && sel.id === ev.board_id ? { kind: "boards" } : sel);
      } else if (ev.type === "canvas_upsert" || ev.type === "canvas_deleted" || ev.type === "canvas_scene") {
        refreshCanvases();
        if (ev.type === "canvas_deleted") setSelection((sel) => sel.kind === "canvas" && sel.id === ev.canvas_id ? { kind: "canvases" } : sel);
      } else if (ev.type === "user_avatar_updated") {
        avatarStore.applyWsUpdate(ev);  // live avatar refresh everywhere
      }
    });
  }, [subscribe, currentId, refreshChannels, refreshUsers, refreshBoards, refreshCanvases]);

  useEffect(() => { trace("selection", { kind: selection.kind, id: selection.id || null }); },
            [selection.kind, selection.id]);

  const selectionRef = useRef(selection);
  useEffect(() => { selectionRef.current = selection; }, [selection]);

  // Sound on a message we are not already looking at.
  useEffect(() => {
    return subscribe((ev) => {
      if (ev.type !== "message") return;
      const m = ev.data;
      if (!m || m.sender_id === user.id) return;
      if (localStorage.getItem("vs_sound") === "off") return;
      const sel = selectionRef.current;
      const watching = !document.hidden && (
        sel.kind === "channel" ? Boolean(m.channel_id) && sel.id === m.channel_id
        : sel.kind === "dm" ? !m.channel_id && sel.id === m.sender_id
        : false
      );
      if (!watching) notification().catch(() => { /* autoplay blocked */ });
    });
  }, [subscribe, user.id]);

  // Auto-mark read when selection changes
  useEffect(() => {
    if (selection.kind === "channel" && selection.id) {
      api.post("/read", { workspace_id: currentId, channel_id: selection.id }).then(refreshUnread).catch(() => {});
    } else if (selection.kind === "dm" && selection.id) {
      api.post("/read", { workspace_id: currentId, dm_partner_id: selection.id }).then(refreshUnread).catch(() => {});
    }
  }, [selection, currentId, refreshUnread]);

  const sendAIToChannel = useCallback(async ({ channel_id, content }) => {
    await api.post("/messages", { workspace_id: currentId, channel_id, content });
  }, [currentId]);

  const saveAISnippet = useCallback(async ({ title, language, code }) => {
    await api.post("/snippets", { workspace_id: currentId, title, language, code });
  }, [currentId]);

  // Start a call in a channel or direct conversation, announce it, then open
  // the panel with a JWT issued for the current user.
  const startCall = useCallback(async (target, customRoom, mode = "channel") => {
    if (!target?.id) return;
    try {
      const payload = mode === "dm"
        ? { workspace_id: currentId, recipient_id: target.id, room: customRoom || null }
        : { channel_id: target.id, room: customRoom || null };
      const { data } = await api.post("/call/start", payload);
      const roomName = data.room_name;
      const tok = await api.get("/call/token", { params: { room: roomName } });
      setCall({ roomName, token: tok.data.token, channelName: target.name });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apelul nu a pornit");
    }
  }, [currentId]);

  // Join an existing call (from the header banner or an in-channel call card):
  // fetch our own JWT for that room and open the panel. No new announcement.
  const joinCall = useCallback(async (roomName, channelName) => {
    if (!roomName) return;
    try {
      const tok = await api.get("/call/token", { params: { room: roomName } });
      setCall({ roomName, token: tok.data.token, channelName });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut intra în apel");
    }
  }, []);

  const inviteToCall = useCallback(async (recipientIds) => {
    if (!call?.roomName || !recipientIds?.length) return;
    const { data } = await api.post("/call/invite", {
      workspace_id: currentId,
      room: call.roomName,
      recipient_ids: recipientIds,
    });
    toast.success(`${data.invited} invitație${data.invited === 1 ? "" : "i"} trimisă`);
  }, [call?.roomName, currentId]);

  // Cmd/Ctrl+K from anywhere in the workspace. Nothing else is intercepted, so
  // the browser's own shortcuts keep working.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key?.toLowerCase() !== "k") return;
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      e.preventDefault();
      setPaletteOpen((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ----- Incoming call -----------------------------------------------------
  // The whole flow lives in useIncomingCall: the page cannot be rendered in a
  // test without half the app, and this is the part that failed in real use.
  const { incoming, clear: clearIncoming, enableNotifications, declineNotifications } =
    useIncomingCall({ subscribe, userId: user?.id, inCall: !!call });

  const acceptIncoming = useCallback(() => {
    if (!incoming) return;
    const label = incoming.kind === "dm" ? (incoming.from?.name || "Apel") : "Apel de grup";
    clearIncoming();
    joinCall(incoming.room_name, label);
  }, [incoming, joinCall, clearIncoming]);

  const dismissIncoming = clearIncoming;

  const callBackIncoming = useCallback(async () => {
    const target = users.find((u) => u.id === incoming?.from?.id);
    clearIncoming();
    if (!target) return;
    ring({ subtle: true }).catch(() => { /* autoplay blocked */ });
    try { await startCall(target, null, "dm"); } finally { stopRing(); }
  }, [incoming, users, startCall, clearIncoming]);

  const callTarget =
    selection.kind === "channel" ? channels.find((c) => c.id === selection.id)
    : selection.kind === "dm" ? users.find((u) => u.id === selection.id)
    : null;

  const startCallHere = useCallback(async () => {
    if (!callTarget) return;
    ring({ subtle: true }).catch(() => { /* autoplay blocked */ });
    try { await startCall(callTarget, null, selection.kind === "dm" ? "dm" : "channel"); }
    finally { stopRing(); }
  }, [callTarget, selection.kind, startCall]);

  const runOpsCommand = useCallback(async (command) => {
    const channelId = opsConfig?.channel_id;
    if (!channelId) return;
    selectAndCloseMobileNav({ kind: "channel", id: channelId });  // so the output is in view
    try {
      await api.post("/ops/run", { command, channel_id: channelId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comanda nu a reușit");
    }
  }, [opsConfig?.channel_id, selectAndCloseMobileNav]);

  const openAI = useCallback((prompt) => {
    setAiPrompt(prompt || "");
    setPanel("atena");
    localStorage.setItem("vs_side_panel", "atena");
    setAiOpen(true);
  }, []);

  // The mobile navigation covers the page; Escape backs out of it like the
  // backdrop tap does.
  useEscape(mobileSidebarOpen, () => setMobileSidebarOpen(false));

  const showPanel = useCallback((which) => {
    setPanel(which);
    localStorage.setItem("vs_side_panel", which);
  }, []);

  // The Atena button on a board or whiteboard opens the side panel on her, so
  // she arrives already knowing what is on screen.
  const askAtena = useCallback(() => {
    showPanel("atena");
    setAiOpen(true);
  }, [showPanel]);

  // #ops is owner-only, so the palette must not offer its commands to anyone else.
  const paletteOpsCommands = myRole === "owner" && opsConfig?.channel_id ? (opsConfig.commands || []) : [];

  // Same set the tracker header calls "active" — the badge used to count only
  // Open + Reopened, so it disagreed with the header on the same screen.
  const feedbackActiveCount = feedbackTickets.filter((t) => ACTIVE_STATUSES.has(t.status)).length;
  const feedbackAwaitingCount = feedbackTickets.filter((t) => t.status === AWAITING_CONFIRMATION).length;

  // The incoming-call card belongs to every layout this component can return,
  // not only the main one. A call arriving while someone is in a popped-out
  // board window is exactly the case where they do not know to join.
  const callCard = incoming ? (
    <IncomingCall
      call={incoming}
      onAccept={acceptIncoming}
      onDecline={dismissIncoming}
      onDismiss={dismissIncoming}
      onCallBack={incoming.kind === "dm" ? callBackIncoming : null}
      onEnableNotifications={enableNotifications}
      onDeclineNotifications={declineNotifications}
    />
  ) : null;

  // A standalone window has no sidebar to open Atena from, so she docks inside
  // it. Same panel, same context, minus the switch to VIBE.AI -- there is no
  // other panel here to switch to.
  const standaloneAtena = (ctx) => aiOpen && (
    <aside className="w-full max-w-[420px] flex flex-col border-l border-brand/30 bg-bg flex-shrink-0">
      <AtenaPanel workspaceId={currentId} {...ctx} onClose={() => setAiOpen(false)} />
    </aside>
  );

  if (standaloneBoard && selection.kind === "canvas" && selection.id) {
    const canvas = canvases.find((c) => c.id === selection.id) || { id: selection.id };
    return (
      <div className="h-screen bg-bg text-fg flex min-h-0">
        <div className="flex-1 min-w-0">
          <CanvasPage key={selection.id} canvasId={selection.id} workspaceId={currentId} standalone
            currentUser={user} users={users} myRole={myRole} subscribe={subscribe}
            onAskAtena={() => setAiOpen((v) => !v)} onBack={() => window.close()} />
        </div>
        {standaloneAtena({ canvas })}
        {callCard}
      </div>
    );
  }
  if (standaloneBoard && selection.kind === "board" && selection.id) {
    const board = boards.find((b) => b.id === selection.id) || { id: selection.id };
    return (
      <div className="h-screen bg-bg text-fg flex min-h-0">
        <div className="flex-1 min-w-0">
          <BoardPage key={selection.id} boardId={selection.id} workspaceId={currentId} standalone
            currentUser={user} users={users} myRole={myRole} subscribe={subscribe}
            onAskAtena={() => setAiOpen((v) => !v)} onBack={() => window.close()} />
        </div>
        {standaloneAtena({ board })}
        {callCard}
      </div>
    );
  }

  return (
    <div
      className="h-screen flex bg-bg text-fg overflow-hidden"
      style={{ backgroundImage: "var(--brand-bg, none)" }}
    >
      {mobileSidebarOpen && (
        <button
          type="button"
          aria-label="Închide navigarea"
          data-testid="mobile-sidebar-backdrop"
          onClick={() => setMobileSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
        />
      )}
      <Sidebar
        channels={channels}
        users={users}
        projects={projects}
        selection={selection}
        onSelect={selectAndCloseMobileNav}
        onChannelsChanged={refreshChannels}
        onOpenSearch={() => setPaletteOpen(true)}
        myRole={myRole}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        feedbackCount={feedbackActiveCount}
        feedbackAwaitingCount={feedbackAwaitingCount}
      />

      <main className="relative flex-1 flex flex-col min-w-0 w-full border-l border-line/10">
        <button
          type="button"
          aria-label="Deschide navigarea"
          data-testid="mobile-menu-button"
          onClick={() => setMobileSidebarOpen(true)}
          className="md:hidden absolute left-3 top-3 z-30 p-2 bg-surface border border-line/10 text-fg/80 hover:text-fg"
        >
          <Menu className="w-4 h-4" />
        </button>
        {selection.kind === "channel" && (
          <ChatView
            key={`c-${selection.id}`} mode="channel"
            target={channels.find((c) => c.id === selection.id)}
            workspaceId={currentId} currentUser={user} users={users}
            isOwner={myRole === "owner"} myRole={myRole} opsConfig={opsConfig}
            assistantOpen={aiOpen} assistantName={panel === "atena" ? "Atena" : "VIBE.AI"}
            onToggleAssistant={() => setAiOpen((v) => !v)}
            subscribe={subscribe} onOpenFeedback={openFeedbackTicket}
            onStartCall={startCall} onJoinCall={joinCall}
          />
        )}
        {selection.kind === "dm" && (
          <ChatView
            key={`d-${selection.id}`} mode="dm"
            target={users.find((u) => u.id === selection.id)}
            workspaceId={currentId} currentUser={user} users={users}
            isOwner={myRole === "owner"} myRole={myRole} opsConfig={opsConfig}
            assistantOpen={aiOpen} assistantName={panel === "atena" ? "Atena" : "VIBE.AI"}
            onToggleAssistant={() => setAiOpen((v) => !v)}
            subscribe={subscribe} onOpenFeedback={openFeedbackTicket}
            onStartCall={startCall} onJoinCall={joinCall}
          />
        )}
        {selection.kind === "projects" && (
          <ProjectsView
            workspaceId={currentId}
            projects={projects} users={users} currentUser={user}
            onProjectsChanged={refreshProjects} subscribe={subscribe}
          />
        )}
        {selection.kind === "documents" && (
          <DocumentsView workspaceId={currentId} currentUser={user} channels={channels}
            boards={boards} canvases={canvases} subscribe={subscribe} />
        )}
        {selection.kind === "snippets" && <SnippetsView workspaceId={currentId} currentUser={user} />}
        {selection.kind === "members" && <MembersView workspaceId={currentId} currentUser={user} onUsersChanged={refreshUsers} />}
        {selection.kind === "feedback" && (
          <FeedbackView projects={projects}
            workspaceId={currentId} currentUser={user} users={users} channels={channels}
            isOwner={myRole === "owner"} myRole={myRole} tickets={feedbackTickets} loading={feedbackLoading}
            onUpsert={upsertTicket}
            detailId={feedbackDetailId}
            onOpenDetail={setFeedbackDetailId}
            onCloseDetail={() => setFeedbackDetailId(null)}
            onGoToChannel={goToChannel}
          />
        )}
        {selection.kind === "boards" && (
          <BoardsView workspaceId={currentId} boards={boards} loading={boardsLoading}
            onOpen={(id) => choose({ kind: "board", id })}
            onCreated={(b) => { refreshBoards(); choose({ kind: "board", id: b.id }); }} />
        )}
        {selection.kind === "board" && selection.id && (
          <BoardPage key={selection.id} boardId={selection.id} workspaceId={currentId}
            currentUser={user} users={users} myRole={myRole} subscribe={subscribe}
            onAskAtena={askAtena} onBack={() => choose({ kind: "boards" })} />
        )}
        {selection.kind === "canvases" && (
          <CanvasesView workspaceId={currentId} canvases={canvases} loading={canvasesLoading} users={users}
            onOpen={(id) => choose({ kind: "canvas", id })}
            onCreated={(c) => { refreshCanvases(); choose({ kind: "canvas", id: c.id }); }} />
        )}
        {selection.kind === "canvas" && selection.id && (
          <CanvasPage key={selection.id} canvasId={selection.id} workspaceId={currentId}
            currentUser={user} users={users} myRole={myRole} subscribe={subscribe}
            onAskAtena={askAtena} onBack={() => choose({ kind: "canvases" })} />
        )}
        {selection.kind === "settings" && (
          <SettingsView currentUser={user} workspaceId={currentId} isOwner={myRole === "owner"} />
        )}
      </main>

      {aiOpen ? (
        <aside className="fixed inset-y-0 right-0 z-40 w-full max-w-[420px] flex flex-col border-l border-brand/30 bg-bg xl:relative xl:inset-auto xl:z-auto xl:w-[420px] xl:max-w-none">
          {panel === "atena" ? (
            <AtenaPanel
              workspaceId={currentId}
              channel={selection.kind === "channel" ? channels.find((c) => c.id === selection.id) : null}
              board={selection.kind === "board" ? boards.find((b) => b.id === selection.id) : null}
              canvas={selection.kind === "canvas" ? canvases.find((c) => c.id === selection.id) : null}
              onClose={() => setAiOpen(false)}
              onSwitchToVibe={() => showPanel("vibe")}
            />
          ) : (
            <AIPanel
              onClose={() => setAiOpen(false)}
              onSwitchToAtena={() => showPanel("atena")}
              channels={channels}
              onSendToChannel={sendAIToChannel}
              onSaveSnippet={saveAISnippet}
              workspaceId={currentId}
              initialPrompt={aiPrompt}
            />
          )}
        </aside>
      ) : (
        <button
          data-testid="open-ai-panel-button"
          onClick={() => setAiOpen(true)}
          className="flex absolute right-4 bottom-4 z-30 items-center gap-2 px-3 py-2 bg-accent-gradient text-fg font-medium glow-amber hover:opacity-90 transition-opacity"
        >
          <Sparkles className="w-4 h-4" /> {panel === "atena" ? "ATENA" : "VIBE"}
        </button>
      )}

      {callCard}
      {call && (
        <CallPanel
          roomName={call.roomName}
          token={call.token}
          channelName={call.channelName}
          currentUser={user}
          users={users}
          onInvite={inviteToCall}
          onClose={() => setCall(null)}
        />
      )}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        workspaceId={currentId}
        channels={channels}
        users={users}
        tickets={feedbackTickets}
        opsCommands={paletteOpsCommands}
        canCall={Boolean(callTarget)}
        onSelectChannel={(id) => selectAndCloseMobileNav({ kind: "channel", id })}
        onSelectDm={(id) => selectAndCloseMobileNav({ kind: "dm", id })}
        onOpenMembers={() => selectAndCloseMobileNav({ kind: "members" })}
        onOpenTicket={openFeedbackTicket}
        onReportBug={() => setBugOpen(true)}
        onStartCall={startCallHere}
        onRunOpsCommand={runOpsCommand}
        onOpenAI={openAI}
      />
      {bugOpen && (
        <FeedbackModal
          workspaceId={currentId}
          channelId={selection.kind === "channel" ? selection.id : null}
          onClose={() => setBugOpen(false)}
        />
      )}
      <PostLoginUpdate workspaceId={currentId} />
    </div>
  );
}
